import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Layers, Fingerprint, Cpu, Code2, Palette, Sparkles, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const AnimatedCounter = ({ end, suffix = "", duration = 2000, pad = false, endDescription = "" }: { end: number, suffix?: string, duration?: number, pad?: boolean, endDescription?: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.ceil(start));
            }
          }, 16);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return (
    <div ref={ref} className="text-3xl sm:text-4xl font-display font-black text-slate-900" aria-label={`${end}${suffix} ${endDescription}`}>
      {pad ? String(count).padStart(2, '0') : count}{suffix}
    </div>
  );
};

const About: React.FC = () => {
  const [isColor, setIsColor] = useState(false);
  const [activeTab, setActiveTab] = useState<'dev' | 'video' | 'design'>('dev');
  const { t, isUrdu } = useLanguage();

  return (
    <section 
      id="about" 
      aria-label="About Taibe Javed - Professional Background & Expertise"
      className="py-14 sm:py-20 bg-slate-50 relative overflow-hidden w-full"
    >
      {/* Ambient background glows */}
      <div className="absolute top-1/4 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-indigo-100/60 blur-[80px] sm:blur-[120px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-10 right-0 sm:right-10 w-64 h-64 sm:w-96 sm:h-96 bg-blue-100/60 blur-[80px] sm:blur-[120px] rounded-full pointer-events-none"></div>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 relative z-10 reveal">
        <div className="grid lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-14 items-center">
          
          {/* Content side (LEFT) - Col 7 */}
          <div className="lg:col-span-7 space-y-6 text-center lg:text-left flex flex-col justify-center items-center lg:items-start w-full reveal reveal-stagger max-w-[100vw] overflow-hidden sm:overflow-visible">
            <div className="space-y-4 w-full flex flex-col items-center lg:items-start">
              <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-white border border-slate-200 rounded-full shadow-sm">
                <Sparkles size={14} className="text-indigo-600" />
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-700">
                  {t.about.badge}
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-sans font-extrabold leading-[1.2] sm:leading-[1.1] text-slate-900 tracking-tight w-full break-words">
                {t.about.headingPrefix}{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">
                  {t.about.headingHighlight}
                </span>
              </h2>

              <p className="text-slate-600 text-base sm:text-lg font-normal leading-relaxed max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
                {t.about.bio}
              </p>
            </div>

            {/* Interactive Pillar Selector Tabs */}
            <div className="w-full space-y-4 my-2 max-w-[calc(100vw-2rem)] sm:max-w-none mx-auto">
              <div className="flex items-center gap-2 border-b border-slate-200 pb-2 overflow-x-auto no-scrollbar scroll-smooth snap-x w-full">
                {[
                  { id: 'dev', label: t.about.tabDev, icon: Code2 },
                  { id: 'video', label: t.about.tabVideo, icon: Cpu },
                  { id: 'design', label: t.about.tabDesign, icon: Palette },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap snap-start shrink-0 cursor-pointer ${
                        activeTab === tab.id
                          ? 'bg-slate-900 text-white shadow-md'
                          : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200'
                      }`}
                    >
                      <Icon size={14} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Tab Content Panel */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 text-left shadow-sm min-h-[120px] w-full">
                {activeTab === 'dev' && (
                  <div className="space-y-3 animate-fadeIn w-full">
                    <h4 className="text-slate-900 font-bold text-sm sm:text-base flex items-center gap-2">
                      <Code2 size={16} className="text-indigo-600 shrink-0" />
                      {t.about.devTitle}
                    </h4>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      {t.about.devDesc}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-1 w-full">
                      {t.about.devTags.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px] sm:text-[11px] text-slate-700 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-200 min-w-0 w-full overflow-hidden">
                          <CheckCircle2 size={12} className="text-indigo-600 shrink-0" />
                          <span className="truncate">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'video' && (
                  <div className="space-y-3 animate-fadeIn w-full">
                    <h4 className="text-slate-900 font-bold text-sm sm:text-base flex items-center gap-2">
                      <Cpu size={16} className="text-indigo-600 shrink-0" />
                      {t.about.videoTitle}
                    </h4>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      {t.about.videoDesc}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-1 w-full">
                      {t.about.videoTags.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px] sm:text-[11px] text-slate-700 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-200 min-w-0 w-full overflow-hidden">
                          <CheckCircle2 size={12} className="text-indigo-600 shrink-0" />
                          <span className="truncate">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'design' && (
                  <div className="space-y-3 animate-fadeIn w-full">
                    <h4 className="text-slate-900 font-bold text-sm sm:text-base flex items-center gap-2">
                      <Palette size={16} className="text-indigo-600 shrink-0" />
                      {t.about.designTitle}
                    </h4>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      {t.about.designDesc}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-1 w-full">
                      {t.about.designTags.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px] sm:text-[11px] text-slate-700 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-200 min-w-0 w-full overflow-hidden">
                          <CheckCircle2 size={12} className="text-indigo-600 shrink-0" />
                          <span className="truncate">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Statistics Counters & CTA Button */}
            <div className="w-full space-y-4 pt-4 border-t border-slate-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col justify-center text-center sm:text-left">
                  <AnimatedCounter end={1} endDescription="Year of expertise" pad={true} />
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1">
                    {t.about.statYears}
                  </div>
                </div>
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col justify-center text-center sm:text-left">
                  <AnimatedCounter end={30} suffix="+" endDescription="Deployments completed" />
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1">
                    {t.about.statDeployments}
                  </div>
                </div>
              </div>

              <div className="flex justify-center lg:justify-start">
                <button 
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  aria-label="Consult protocol for a new project"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs uppercase tracking-[0.2em] px-8 py-3.5 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 active:scale-95 group shrink-0 max-w-full cursor-pointer"
                >
                  <span className="truncate">{t.about.btnStart}</span>
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform shrink-0" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          {/* Portrait side (RIGHT) - Col 5 */}
          <div className="lg:col-span-5 relative group w-full max-w-xs sm:max-w-md lg:max-w-none mx-auto lg:mx-0 flex flex-col h-auto sm:h-full min-h-[350px] sm:min-h-[520px] lg:min-h-0 mt-6 lg:mt-0">
            <div 
              className="relative z-10 w-full h-full min-h-[400px] lg:min-h-full rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden bg-white shadow-xl border border-slate-200 cursor-pointer flex flex-col"
              onClick={() => setIsColor(!isColor)}
              role="button"
              aria-label="Toggle portrait color effect"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsColor(!isColor); }}
            >
              <img 
                src="https://i.pinimg.com/736x/a5/19/52/a51952b60084d169d7a1b51efd18c5d4.jpg" 
                alt="Muhammad Taibe Javed - WordPress Web Developer and AI Video Editor" 
                width="600"
                height="800"
                loading="lazy"
                decoding="async"
                className={`w-full h-full object-cover object-[center_20%] flex-1 transition-all duration-700 ${isColor ? 'grayscale-0 brightness-100' : 'grayscale brightness-90'} group-hover:grayscale-0 group-hover:brightness-100`}
              />
              
              {/* Overlay Glass UI */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-lg">
                 <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-sm flex-shrink-0">
                      <Cpu size={22} />
                    </div>
                    <div>
                      <div className="text-slate-900 font-bold text-base">{t.about.cardName}</div>
                      <div className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">{t.about.cardRole}</div>
                    </div>
                 </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default About;